import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Typography, Card, CardContent, Stack, Box, Button, Chip, Avatar, Divider,
    Grid, CircularProgress,
} from '@mui/material';
import { Icon } from '@iconify/react';
import arrowForwardFill from '@iconify/icons-eva/arrow-forward-fill';
import syndicationApi from 'src/utils/syndicationApi';

const STATUS_COLORS = {
    active: 'success',
    presentation: 'primary',
    qa_phase: 'info',
    commitment_phase: 'warning',
    allocating: 'secondary',
    closed: 'default',
};

const STATUS_LABELS = {
    active: 'Active',
    presentation: 'Presentation',
    qa_phase: 'Q&A Phase',
    commitment_phase: 'Commitment',
    allocating: 'Allocating',
    closed: 'Closed',
};

const INVESTOR_NEXT_STEPS = {
    active: { label: 'Review deal materials', color: 'info.main' },
    presentation: { label: 'Review presentation materials', color: 'primary.main' },
    qa_phase: { label: 'Submit your questions in the Deal Room', color: 'warning.main' },
    commitment_phase: { label: 'Submit your commitment before deadline', color: 'error.main' },
    allocating: { label: 'Allocation in progress \u2014 awaiting confirmation', color: 'secondary.main' },
    closed: { label: 'Deal closed \u2014 check your portfolio', color: 'success.main' },
};

export default function InvestorDashboard() {
    const navigate = useNavigate();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [deals, setDeals] = useState([]);
    const [dealsLoading, setDealsLoading] = useState(true);

    useEffect(() => {
        syndicationApi.get('investor/profile/me/')
            .then((response) => {
                setProfile(response.data?.data);
            })
            .catch(() => {})
            .finally(() => setLoading(false));

        syndicationApi.get('investor/deals/')
            .then((res) => {
                setDeals(res.data?.data || []);
            })
            .catch(() => {})
            .finally(() => setDealsLoading(false));
    }, []);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    const activeDeals = deals.filter(d => d.status !== 'closed');
    const closedDeals = deals.filter(d => d.status === 'closed');

    return (
        <Box>
            <Stack spacing={3}>
                {/* Profile Summary */}
                <Card sx={{ p: 3 }}>
                    <Stack direction="row" spacing={3} alignItems="center">
                        <Avatar sx={{ width: 64, height: 64, bgcolor: 'primary.main', fontSize: 24 }}>
                            {profile?.first_name?.[0]}{profile?.last_name?.[0]}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="h6">
                                {profile?.first_name} {profile?.last_name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {profile?.email} &bull; {profile?.phone}
                            </Typography>
                            {profile?.organization_name && (
                                <Typography variant="body2" color="text.secondary">
                                    {profile?.organization_name}
                                    {profile?.investor_type && ` (${profile.investor_type.replace(/_/g, ' ')})`}
                                </Typography>
                            )}
                        </Box>
                        <Chip
                            label={profile?.totp_enabled ? '2FA Enabled' : '2FA Disabled'}
                            color={profile?.totp_enabled ? 'success' : 'warning'}
                            variant="outlined"
                            size="small"
                        />
                    </Stack>
                </Card>

                <Divider />

                    {/* Active Deals */}
                    <Box>
                        <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
                            Active Deals ({activeDeals.length})
                        </Typography>
                        {dealsLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : activeDeals.length === 0 ? (
                            <Card sx={{ p: 4, textAlign: 'center' }}>
                                <Typography variant="body1" color="text.secondary">
                                    No active deals right now. You'll see invited deals here once they are shared with you.
                                </Typography>
                            </Card>
                        ) : (
                            <Grid container spacing={2}>
                                {activeDeals.map((deal) => (
                                    <Grid item xs={12} sm={6} md={4} key={deal.id}>
                                        <Card
                                            sx={{
                                                cursor: 'pointer', transition: 'all 0.2s',
                                                '&:hover': { boxShadow: 6, transform: 'translateY(-2px)' },
                                                height: '100%', display: 'flex', flexDirection: 'column',
                                            }}
                                            onClick={() => navigate(`/investor/deals/${deal.deal_code}`)}
                                        >
                                            <CardContent sx={{ flex: 1 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1, mr: 1 }}>
                                                        {deal.title}
                                                    </Typography>
                                                    <Chip
                                                        label={STATUS_LABELS[deal.status] || deal.status?.replace(/_/g, ' ')}
                                                        color={STATUS_COLORS[deal.status] || 'default'}
                                                        size="small"
                                                    />
                                                </Box>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                    {deal.company_name}
                                                </Typography>
                                                <Stack spacing={0.75}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <Typography variant="caption" color="text.secondary">Total Raise</Typography>
                                                        <Typography variant="caption" fontWeight={600}>
                                                            {deal.currency_label} {Number(deal.total_raise || 0).toLocaleString()}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <Typography variant="caption" color="text.secondary">Co-investor Allocation</Typography>
                                                        <Typography variant="caption" fontWeight={600}>
                                                            {deal.currency_label} {Number(deal.coinvestor_allocation || 0).toLocaleString()}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <Typography variant="caption" color="text.secondary">Instrument</Typography>
                                                        <Typography variant="caption" fontWeight={600}>
                                                            {deal.instrument_type?.replace(/_/g, ' ') || '-'}
                                                        </Typography>
                                                    </Box>
                                                    {deal.commitment_deadline && (
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                            <Typography variant="caption" color="text.secondary">Deadline</Typography>
                                                            <Typography variant="caption" fontWeight={600}>
                                                                {new Date(deal.commitment_deadline).toLocaleDateString()}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Stack>
                                                {INVESTOR_NEXT_STEPS[deal.status] && (
                                                    <Box sx={{ bgcolor: 'grey.50', borderRadius: 1, px: 1.5, py: 0.75, mt: 1.5, borderLeft: 3, borderColor: INVESTOR_NEXT_STEPS[deal.status].color }}>
                                                        <Typography variant="caption" sx={{ fontWeight: 600, color: INVESTOR_NEXT_STEPS[deal.status].color }}>
                                                            {INVESTOR_NEXT_STEPS[deal.status].label}
                                                        </Typography>
                                                    </Box>
                                                )}
                                                <Button
                                                    fullWidth variant="outlined" size="small" sx={{ mt: 1.5 }}
                                                    endIcon={<Icon icon={arrowForwardFill} />}
                                                >
                                                    View Deal Room
                                                </Button>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </Box>

                    {/* Portfolio / Closed Deals */}
                    <Box>
                        <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
                            My Portfolio ({closedDeals.length})
                        </Typography>
                        {dealsLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : closedDeals.length === 0 ? (
                            <Card sx={{ p: 4, textAlign: 'center' }}>
                                <Typography variant="body1" color="text.secondary">
                                    Your investment portfolio summary will appear here once deals are closed.
                                </Typography>
                            </Card>
                        ) : (
                            <Grid container spacing={2}>
                                {closedDeals.map((deal) => (
                                    <Grid item xs={12} sm={6} md={4} key={deal.id}>
                                        <Card
                                            sx={{
                                                cursor: 'pointer', transition: 'all 0.2s',
                                                '&:hover': { boxShadow: 4 },
                                            }}
                                            onClick={() => navigate(`/investor/deals/${deal.deal_code}`)}
                                        >
                                            <CardContent>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1, mr: 1 }}>
                                                        {deal.title}
                                                    </Typography>
                                                    <Chip label="Closed" color="success" size="small" />
                                                </Box>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                    {deal.company_name}
                                                </Typography>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <Typography variant="caption" color="text.secondary">Total Raise</Typography>
                                                    <Typography variant="caption" fontWeight={600}>
                                                        {deal.currency_label} {Number(deal.total_raise || 0).toLocaleString()}
                                                    </Typography>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </Box>
            </Stack>
        </Box>
    );
}
